import { cn } from "@/lib/utils";
import { DEFAULT_SELLER_PAYOUT_HOURS, SELLER_PAYOUT_HOURS } from "@/lib/businessDays";
import { formatDtFr, resolveTransactionTracking } from "@/lib/transactionTracking";
import LiveCountdown from "@/components/shared/LiveCountdown";
import { AlertTriangle, Banknote, Clock, Lock, Scale, Shield } from "lucide-react";

export default function TransactionTrackingCard({
  tx,
  litiges = [],
  role = "buyer",
  className,
  compact = false,
}) {
  if (!tx) return null;
  const tr = resolveTransactionTracking(tx, { litiges, role });
  const urgent = tr.urgency === "high" || tr.needs_attention;

  const rows = [
    { icon: Banknote, label: "Où est l’argent", value: tr.money_location_label },
    {
      icon: Clock,
      label: role === "seller" ? "Versement" : "Étape",
      value: tr.pipeline_label,
    },
  ];

  if (role === "buyer") {
    rows.push({
      icon: tr.buyer_access === "unlocked" ? Shield : Lock,
      label: "Coordonnées",
      value: tr.buyer_access_label,
      tone: tr.buyer_access === "unlocked" ? "ok" : "muted",
    });
  }

  if (tr.capture_confirmed_at && role !== "buyer") {
    rows.push({
      icon: Clock,
      label: "RDV confirmé",
      value: formatDtFr(tr.capture_confirmed_at),
    });
  }

  const payoutHoursLabel = tr.payout_hours || SELLER_PAYOUT_HOURS || DEFAULT_SELLER_PAYOUT_HOURS;

  if (tr.seller_payout_available_at && (role === "seller" || role === "admin")) {
    const passed = tr.hours_until_payout != null && tr.hours_until_payout <= 0;
    rows.push({
      icon: Clock,
      label: `Délai sécurité (${payoutHoursLabel} h)`,
      value: passed ? (
        `Écoulé — versement ${tr.payout_due ? "en attente d’exécution" : "selon Stripe"}`
      ) : (
        <span className="block space-y-0.5">
          <span className="block">
            Reste :{" "}
            <LiveCountdown
              targetIso={tr.seller_payout_available_at}
              doneLabel="00:00:00"
              className="font-semibold text-slate-900"
            />
          </span>
          <span className="block text-xs font-normal text-slate-500">
            Fin le {formatDtFr(tr.seller_payout_available_at)}
          </span>
        </span>
      ),
    });
  }

  if (tr.dispute_window_ends_at && tr.dispute_window_open !== false && role === "buyer") {
    const disputePassed = tr.hours_until_dispute_end != null && tr.hours_until_dispute_end <= 0;
    rows.push({
      icon: Scale,
      label: `Fenêtre litige (${payoutHoursLabel} h)`,
      value: disputePassed ? (
        "Fenêtre expirée"
      ) : (
        <span className="block space-y-0.5">
          <span className="block">
            Reste :{" "}
            <LiveCountdown
              targetIso={tr.dispute_window_ends_at}
              doneLabel="00:00:00"
              className="font-semibold text-slate-900"
            />
          </span>
          <span className="block text-xs font-normal text-slate-500">
            Jusqu’au {formatDtFr(tr.dispute_window_ends_at)}
          </span>
        </span>
      ),
    });
  }

  if (tr.next_step) {
    rows.push({
      icon: Clock,
      label: "Prochaine étape",
      value: [tr.next_step, tr.next_step_eta].filter(Boolean).join(" · "),
    });
  }

  return (
    <div
      className={cn(
        "rounded-xl border p-4 space-y-3",
        urgent ? "border-amber-200 bg-amber-50/90" : "border-slate-200 bg-slate-50/80",
        className,
      )}
      data-testid="transaction-tracking-card"
    >
      {tr.needs_attention && tr.attention_reasons?.length ? (
        <div className="flex gap-2 text-sm text-amber-950">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <ul className="list-disc pl-4 space-y-0.5">
            {tr.attention_reasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className={cn("grid gap-3", compact ? "grid-cols-1" : "sm:grid-cols-2")}>
        {rows.map(({ icon: Icon, label, value, tone }) => (
          <div key={label} className="flex gap-2 min-w-0">
            <Icon
              className={cn(
                "h-4 w-4 shrink-0 mt-0.5",
                tone === "ok" ? "text-emerald-600" : urgent ? "text-amber-700" : "text-slate-500",
              )}
              aria-hidden
            />
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
              <div className="text-sm font-medium text-slate-900 break-words">{value}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
